import { SelectHTMLAttributes, forwardRef, useMemo } from 'react';
import { sortOptionsByLabel } from './optionOrder';

/**
 * Select — single-select dropdown atom. Consumes `enumOptions: [{value, label}]`
 * from the schema endpoint (Phase 11 Block 2 PR 1) or any other [{value,label}] array.
 *
 * Options render alphabetically by label (case- and accent-insensitive), with the
 * placeholder and any empty-value row first; see ./optionOrder. A list whose order is its
 * meaning (stages, years, a status ladder) passes `preserveOrder`. The native control
 * already jumps to the first option starting with a typed letter.
 */

export interface SelectOption {
  value: string | number;
  label: string;
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  options: SelectOption[];
  placeholder?: string;
  invalid?: boolean;
  size?: 'sm' | 'md' | 'lg';
  /** Render the options in the order given instead of alphabetically. */
  preserveOrder?: boolean;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { options, placeholder, invalid, size = 'md', preserveOrder = false, style, ...rest },
  ref,
) {
  const ordered = useMemo(() => (preserveOrder ? options : sortOptionsByLabel(options)), [options, preserveOrder]);
  const padY = size === 'sm' ? '6px' : size === 'lg' ? '10px' : '8px';
  const padX = size === 'sm' ? '10px' : '12px';
  const fs = size === 'sm' ? 'var(--fs-sm)' : size === 'lg' ? 'var(--fs-lg)' : 'var(--fs-md)';
  return (
    <select
      ref={ref}
      aria-invalid={invalid || undefined}
      style={{
        fontFamily: 'inherit',
        fontSize: fs,
        padding: `${padY} ${padX}`,
        border: `1px solid ${invalid ? 'var(--color-status-red)' : 'var(--color-border)'}`,
        borderRadius: 'var(--r-md)',
        background: 'var(--color-card)',
        color: 'var(--color-ink)',
        outline: 'none',
        cursor: 'pointer',
        width: '100%',
        minWidth: 0,
        maxWidth: '100%',
        ...style,
      }}
      {...rest}
    >
      {placeholder && <option value="">{placeholder}</option>}
      {ordered.map(opt => (
        <option key={String(opt.value)} value={opt.value}>{opt.label}</option>
      ))}
    </select>
  );
});
